import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Divider,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { motion } from "framer-motion";
import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { FormProvider, RHFTextField } from "src/components/hook-form";
import useLocales from "src/hooks/useLocales";
import useInterval from "src/hooks/useInterval";
import { SetGeneralCookie } from "src/common/cookie";
import { MixpanelFunction } from "src/mixpanel/mixpanelFunction";
import { store, useSelector } from "src/redux/store";
import {
  getSurveyStatus,
  submitSurvey,
  ISurveyStatusResponse,
} from "src/services/survey/surveyService";
import {
  NPS_DISMISS_COUNT,
  NPS_NEXT_SHOW_AT,
  SNOOZE_DURATION_MS,
  POLL_INTERVAL_MS,
  IFormValues,
  emojis,
} from "./constants";
import {
  scopedCookieName,
  getDismissCount,
  evaluateSurveyVisibility,
  isSignupEligible,
} from "./utils";

export const NPSModal = () => {
  const { translate } = useLocales();
  const [isOpen, setIsOpen] = useState(false);
  const [lastDay, setLastDay] = useState(false);
  const [surveyKey, setSurveyKey] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const methods = useForm<IFormValues>({
    defaultValues: {
      score: null,
      feedback: "",
    },
  });

  const { handleSubmit, setValue, watch, reset } = methods;
  const selectedScore = watch("score");

  const accessToken = useSelector(
    (state) => (state.credential?.userCred as any)?.accessToken,
  );
  const userEmail = useSelector(
    (state) => (state.credential?.userCred as any)?.email,
  );
  const isImpersonatedUser = useSelector(
    (state) => state.organization?.org?.isImpersonatedUser,
  );
  const createDate = useSelector(
    (state) => state.organization?.org?.createDate,
  );
  const shouldPoll =
    Boolean(accessToken) && !isImpersonatedUser && isSignupEligible(createDate);
  // Namespaces all NPS cookies by user so a dismiss/survey-cycle from one
  // account never leaks into another account on the same browser.
  const userKey = userEmail || "anonymous";

  // React 17 doesn't auto-batch dispatches outside event handlers, so during
  // impersonation login `accessToken` can commit a render before
  // `isImpersonatedUser` does, letting a poll slip through. This ref lets an
  // in-flight poll's response check eligibility as of when it resolves,
  // instead of trusting the (possibly stale) `shouldPoll` closure it was
  // kicked off with.
  const shouldPollRef = useRef(shouldPoll);
  shouldPollRef.current = shouldPoll;

  const pollSurveyStatus = useCallback(async () => {
    try {
      const response: any = await getSurveyStatus();
      if (!shouldPollRef.current) return;
      const data: ISurveyStatusResponse | undefined = response?.data;
      if (!data) return;

      setSurveyKey(data.surveyKey);
      const { show, lastChance } = evaluateSurveyVisibility(data, userKey);
      setIsOpen(show);
      setLastDay(lastChance);
    } catch (err) {
      // Leave current visibility state untouched on a failed poll
      console.log("Error fetching survey status", err);
    }
  }, [userKey]);

  useEffect(() => {
    if (shouldPoll) {
      pollSurveyStatus();
    } else {
      // Clear stale visibility so a previously-open modal (e.g. from an
      // impersonated session) doesn't flash open the instant a later,
      // genuinely-eligible session sets shouldPoll back to true.
      setIsOpen(false);
      setLastDay(false);
    }
  }, [shouldPoll, pollSurveyStatus]);

  useInterval(pollSurveyStatus, shouldPoll ? POLL_INTERVAL_MS : null);

  const handleDismiss = () => {
    const currentCount = getDismissCount(userKey);
    const newCount = currentCount + 1;

    // Set next show time 2 days from now
    const nextShowTime = Date.now() + SNOOZE_DURATION_MS;
    const cookieExpiry = new Date(
      nextShowTime + SNOOZE_DURATION_MS,
    ).toISOString();

    console.log(
      `Dismissed NPS survey. Count: ${newCount}, next show at: ${new Date(
        nextShowTime,
      ).toLocaleString()}`,
    );
    SetGeneralCookie(
      scopedCookieName(NPS_DISMISS_COUNT, userKey),
      String(newCount),
      cookieExpiry,
    );
    SetGeneralCookie(
      scopedCookieName(NPS_NEXT_SHOW_AT, userKey),
      String(nextShowTime),
      cookieExpiry,
    );

    setIsOpen(false);
    reset();
  };

  const onSubmitForm = async (data: IFormValues) => {
    if (data.score === null) return;

    setIsSubmitting(true);
    try {
      const response: any = await submitSurvey(surveyKey);
      if (response?.status && response.status >= 400) {
        console.log("Error submitting survey", response);
        return;
      }

      const COMPANY_ID = localStorage.getItem("company_id");
      const userCred: any = store.getState()?.credential?.userCred;

      // Track the NPS survey event via Mixpanel
      MixpanelFunction({
        eventName: "NPS_survey",
        organizationId: COMPANY_ID,
        npsScore: data.score,
        npsFeedback: data.feedback,
        userEmail: userCred?.email,
        userFirstName: userCred?.firstName,
      });

      // NPS cookies are intentionally kept — nps_survey_key prevents the
      // survey from re-showing until the backend issues a new surveyKey
      setIsOpen(false);
      reset();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDialogClose = (
    event: {},
    reason: "backdropClick" | "escapeKeyDown",
  ) => {
    // Prevent closing via backdrop click or escape key
    if (reason === "backdropClick" || reason === "escapeKeyDown") return;
  };

  if (!shouldPoll) return null;

  return (
    <Dialog
      open={isOpen}
      onClose={handleDialogClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: "16px" },
      }}
    >
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmitForm)}>
        {/* Header */}
        <DialogTitle sx={{ pt: 4, px: 3, pb: 2.5 }}>
          <Typography
            variant="h6"
            fontWeight="bold"
            sx={{ fontSize: "1.125rem", color: "text.heading" }}
          >
            {translate("how_likely_are_you_to_recommend_dost_to_a_colleague")}
          </Typography>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ p: 3 }}>
          {/* Emojis */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mb: 4,
              mt: 1,
            }}
          >
            {emojis.map((emoji) => {
              const isSelected = selectedScore === emoji.id;

              return (
                <Box
                  component={motion.div}
                  key={emoji.id}
                  onClick={() => setValue("score", emoji.id)}
                  whileTap={{ scale: 0.9 }}
                  animate={isSelected ? { scale: 1.05 } : { scale: 1, y: 0 }}
                  transition={{ type: "spring", stiffness: 400, damping: 10 }}
                  sx={{
                    width: 92,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    p: 1.5,
                    borderRadius: "12px",
                    cursor: "pointer",
                    transition:
                      "background-color 0.2s ease-in-out, border 0.2s ease-in-out",
                    border: "1px solid",
                    borderColor: isSelected ? "text.input" : "transparent",
                    bgcolor: isSelected ? "text.input" : "transparent",
                    opacity: 1,
                    "&:hover": {
                      bgcolor: isSelected ? "text.input" : "text.input",
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={emoji.icon}
                    alt={emoji.label}
                    sx={{
                      width: 40,
                      height: 40,
                      mb: 1,
                      objectFit: "contain",
                    }}
                  />

                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: isSelected ? "bold" : "normal",
                      color: isSelected ? "primary.main" : "text.label",
                    }}
                  >
                    {translate(emoji.label)}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          {/* Feedback */}
          <Box>
            <Typography
              variant="body2"
              color="text.label"
              sx={{ fontWeight: 600, mb: 1 }}
            >
              {translate("what_the_main_reason_for_your_score")}
            </Typography>
            <RHFTextField
              sx={{ borderRadius: "8px" }}
              name="feedback"
              placeholder={translate("enter_answer")}
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>

        <Divider />

        {/* Footer */}
        <DialogActions
          sx={{ p: 3, pt: 2, justifyContent: "flex-end", gap: 1.5 }}
        >
          {!lastDay && (
            <Button
              onClick={handleDismiss}
              variant="outlined"
              sx={{
                height: "40px",
                px: 3,
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 500,
                color: "text.primary",
                borderColor: "#D1D5DB",
              }}
            >
              {translate("maybe_later")}
            </Button>
          )}
          <LoadingButton
            type="submit"
            variant="contained"
            loading={isSubmitting}
            disabled={selectedScore === null}
            sx={{
              height: "40px",
              px: 4,
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 500,
              bgcolor:
                selectedScore === null
                  ? "action.disabledBackground"
                  : "#0A1929",
              color: selectedScore === null ? "text.disabled" : "common.white",
              "&:hover": {
                bgcolor: "#0f2136",
              },
            }}
          >
            {translate("Submit")}
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
};
